import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { testimonials, faqs } from '@/mocks/data';

export default function AboutPage() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpField = formData.get('phone_alt');
    if (hpField && typeof hpField === 'string' && hpField.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const resp = await fetch('https://readdy.ai/api/form/d9qtuesnlsngrm4lm5tg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([k]) => k !== 'phone_alt')
            .map(([k, v]) => [k, v as string])
        ).toString(),
      });

      const respText = await resp.text();
      let parsed: any;
      try { parsed = JSON.parse(respText); } catch { parsed = null; }

      if (resp.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || respText || '提交失败';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查您的网络连接后重试。');
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于黄瓜视频 - 免费高清视频播放平台',
    description: '黄瓜视频是一个免费高清视频播放平台，提供海量影视内容，支持多终端在线观看。了解我们的品牌故事、用户口碑和联系方式。',
    url: 'https://bicasual.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '黄瓜视频',
      url: 'https://bicasual.cn',
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 黄瓜视频在线观看"
        description="黄瓜视频是一个免费高清视频播放平台，提供海量影视内容，支持多终端在线观看。了解我们的品牌故事、用户口碑和联系方式。"
        keywords="黄瓜视频,关于我们,品牌介绍,视频平台,联系我们"
        canonicalUrl="https://bicasual.cn/about"
        jsonLd={aboutJsonLd}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]} />

            {/* Brand Introduction */}
            <section className="mb-14">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-6">{t('about.brand')}</h1>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="animate-slide-in-left">
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    黄瓜视频成立于2020年，是一家专注于在线视频流媒体服务的创新型互联网企业。我们致力于为广大用户提供高品质、多元化的视频内容，打造一个集娱乐、知识、生活于一体的综合性视频平台。
                  </p>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    经过多年的技术积累和内容沉淀，黄瓜视频已经发展成为拥有超过<strong>5000万</strong>注册用户的大型视频平台。我们与国内外300多家内容提供商建立了深度合作关系，拥有超过<strong>10万小时</strong>的正版视频内容。
                  </p>
                  <p className="text-foreground-700 leading-relaxed">
                    我们的团队由来自互联网、影视、人工智能等领域的资深专家组成，秉承"让每个人都能轻松享受优质视频内容"的理念，持续为用户创造价值。
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden animate-slide-in-right">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20diverse%20team%20collaborating%20on%20large%20screen%20showing%20video%20content%2C%20warm%20natural%20lighting%2C%20collaborative%20atmosphere%2C%20tech%20company%20culture%2C%20professional%20yet%20relaxed%20environment&width=700&height=450&seq=about-brand&orientation=landscape"
                    alt="黄瓜视频团队"
                    title="黄瓜视频团队 - 黄瓜视频在线观看"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Mission & Vision */}
            <section className="mb-14">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 animate-scale-in">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-focus-3-line text-xl"></i>
                  </div>
                  <h3 className="text-lg font-heading text-foreground-900 mb-2">{t('about.mission')}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    让优质视频内容触手可及。我们通过先进的技术手段和丰富的内容生态，打破时间和空间的限制，让每一位用户都能随时随地享受到高品质的视频服务。
                  </p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 animate-scale-in" style={{ animationDelay: '0.1s' }}>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-eye-line text-xl"></i>
                  </div>
                  <h3 className="text-lg font-heading text-foreground-900 mb-2">{t('about.vision')}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    成为全球领先的视频流媒体平台。我们致力于用科技的力量连接内容创作者与观众，构建一个繁荣、多元、健康的视频内容生态系统。
                  </p>
                </div>
              </div>
            </section>

            {/* Testimonials */}
            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">{t('about.reputation')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {testimonials.map((item, index) => (
                  <div key={index} className="p-5 rounded-lg bg-background-100 border border-background-200/70 animate-scale-in" style={{ animationDelay: `${index * 0.08}s` }}>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className={`text-sm ${i < item.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">{item.content}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-sm font-heading text-primary-600">{item.name[0]}</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800">{item.name}</div>
                        <div className="text-xs text-foreground-500">{item.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">{t('about.faq')}</h2>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div key={index} className="rounded-lg bg-background-100 border border-background-200/70 overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    >
                      <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                      <div className={`w-5 h-5 flex items-center justify-center transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-foreground-500"></i>
                      </div>
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-4 animate-fade-in">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">{t('about.contact')}</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800">邮箱</div>
                        <div className="text-sm text-foreground-500">{t('footer.email')}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-phone-line"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800">电话</div>
                        <div className="text-sm text-foreground-500">{t('footer.phone')}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800">地址</div>
                        <div className="text-sm text-foreground-500">{t('footer.address')}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <form onSubmit={handleSubmit} data-readdy-form>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">{t('form.name')}</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">{t('form.email')}</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">{t('form.phone')}</label>
                        <input
                          id="contact-phone"
                          name="phone"
                          type="tel"
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                          placeholder="请输入您的手机号（选填）"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">{t('form.message')}</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          rows={4}
                          required
                          maxLength={500}
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 resize-none"
                          placeholder="请输入您的留言（不超过500字）"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        className="contact-form-hp"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                        aria-hidden="true"
                      />
                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                      >
                        {formStatus === 'submitting' ? '提交中...' : t('form.submit')}
                      </button>
                    </div>
                    {formStatus === 'success' && (
                      <p className="mt-3 text-sm text-green-600">{t('form.success')}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="mt-3 text-sm text-red-600">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}